import useApiActivityLog from "api/activity-log/use-api-activity-log";
import useApiTeamRiskyFeedbackComplete from "api/team/use-api-team-risky-feedback-complete";
import { Button } from "components/button";
import { Content, Modal } from "components/modal";
import { TextArea } from "components/textarea";
import { useEffect, useState } from "react";

import css from "./index.module.scss";

const QUESTION_1_OPTIONS = [
  "I filter them out",
  "I use them sometimes",
  'I\'m not sure what "risky" means',
].sort(() => Math.random() - 0.5);

type Props = {
  onClose: () => void;
};

export const RiskyEmailSurveyModal = ({ onClose }: Props) => {
  const [question1, question1Set] = useState("");
  const [question2, question2Set] = useState("");
  const [screen, screenSet] = useState<"question1" | "question2" | "thankYou">(
    "question1",
  );
  const { mutate: activityLog } = useApiActivityLog();
  const { mutate: riskyFeedbackComplete } = useApiTeamRiskyFeedbackComplete();

  useEffect(() => {
    if (screen === "thankYou") {
      const timer = setTimeout(() => {
        onClose();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [screen, onClose]);

  const handleQuestion1Answer = (answer: string) => {
    question1Set(answer);
    screenSet(answer === "I use them sometimes" ? "question2" : "thankYou");
    riskyFeedbackComplete();
    activityLog({
      description: JSON.stringify({
        question1: answer,
      }),
      type: "risky_feedback_survey1",
    });
  };

  const handleQuestion2Submit = () => {
    activityLog({
      description: JSON.stringify({
        question1,
        question2: question2.trim() || null,
      }),
      type: "risky_feedback_survey2",
    });

    screenSet("thankYou");
  };

  const handleSkip = () => {
    activityLog({
      description: "",
      type: "risky_feedback_survey_skip",
    });
    riskyFeedbackComplete();
    onClose();
  };

  if (screen === "thankYou") {
    return (
      <Modal overlayOpacity={90}>
        <Content className={css.content}>
          <div className={css.thankYou}>Thanks for helping us!</div>
          <Button className={css.closeButton} size="large" onClick={onClose}>
            Close
          </Button>
        </Content>
      </Modal>
    );
  }

  if (screen === "question2") {
    return (
      <Modal overlayOpacity={90}>
        <Content className={css.content}>
          <div className={css.title}>Help us improve</div>
          <div className={css.question}>
            How do you usually use risky emails?
          </div>
          <div className={css.textareaWrapper}>
            <TextArea
              className={css.textarea}
              placeholder="I verify them with ... and use them only if valid"
              rows={5}
              value={question2}
              onChange={(e) => question2Set(e.target.value)}
            />
          </div>
          <Button
            className={css.submitButton}
            size="large"
            onClick={handleQuestion2Submit}
          >
            Submit
          </Button>
        </Content>
      </Modal>
    );
  }

  return (
    <Modal overlayOpacity={90}>
      <Content className={css.content}>
        <div className={css.title}>Help us improve</div>
        <div className={css.question}>
          How do you usually handle emails marked as "risky"?
        </div>
        <div className={css.optionGrid}>
          {QUESTION_1_OPTIONS.map((option) => (
            <button
              className={css.optionItem}
              key={option}
              onClick={() => handleQuestion1Answer(option)}
            >
              {option}
            </button>
          ))}
        </div>
        <button className={css.skip} onClick={handleSkip}>
          Skip the survey
        </button>
      </Content>
    </Modal>
  );
};
